import * as Sentry from "@sentry/browser";
import { Map } from "immutable";
import { delay } from "lodash";
import PubSub from "pubsub-js";
import utf8 from "utf8";

import GlobalFontSizes from "../constants/globalFontSizes";
import {
  DIRECTION_BOTTOM,
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
  DIRECTION_TOP,
  GLOBAL_FILTER_ID,
  MENU_POPOVER_DATE,
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../constants/identifiers";
import PubSubTopics from "../constants/pubSubTopics";
import standardToV3Converter from "../dataConversion/standardFormat/standardToV3Converter";
import { convertV2DataForReadonly } from "../dataConversion/v2Upgrade/upgradeV2DataWithV3Template";
import dialogs from "../dialogs";
import { actionWithAppliedDate } from "../modules/actionWithAppliedDate";
import { appStateOperations, appStateSelectors } from "../modules/appState";
import { autoUpdatesOperations } from "../modules/autoUpdates";
import batchOperations from "../modules/batchOperations";
import { filterOperations } from "../modules/filters";
import { preferencesOperations } from "../modules/preferences";
import { registrationOperations } from "../modules/registration";
// import { registrationOperations } from "../modules/registration";
import rootActionTypes from "../modules/rootActionTypes";
import rootOperations from "../modules/rootOperations";
import { uiStateOperations, uiStateSelectors } from "../modules/uiState";
import { undoRedoOperations } from "../modules/undoRedo";
import { welcomeOperations } from "../modules/welcome";
import notificationMessages from "../notificationMessages";
import updateImageDimensions from "../printing/utils/updateImageDimensions";
import v3ToHarvardConverter from "../store/v3ToHarvardConverter";
import { immutableRecords } from "../types";
import isEditable from "../utils/isEditable";
import preprocessorQuery from "../utils/preprocessorQuery";
import { nativeCommsBroadcastMessageTypes } from ".";
import nativeCommsMessageTypes from "./nativeCommsMessageTypes";

export const publishAction = action => (action ? PubSub.publish(PubSubTopics.externalActionReceived, action) : null);
export const decodeBase64Utf8 = s => utf8.decode(atob(s));
export const decodeAndParseBase64Utf8 = s => JSON.parse(decodeBase64Utf8(s));
export const decodeAndParseBase64 = s => JSON.parse(atob(s));

const isReadonly = preprocessorQuery.isReadonly();

export const loadData = (inData, convertFromV2, convertFromStandard, isSample, isUpdate) => {
  let data = inData;

  if (convertFromV2) {
    if (isReadonly) {
      data = convertV2DataForReadonly(inData);
    } else {
      publishAction(welcomeOperations.showUpgradeWizard(data));
      return;
    }
  }

  if (convertFromStandard) {
    data = standardToV3Converter(data);
  }
  if (!data) {
    dialogs.alertPrompt("File could not be loaded.");
    return;
  }

  //#!if harvard === true
  data = v3ToHarvardConverter(data);
  //#!endif

  //#!if readonly === true
  if (!data) {
    dialogs.alertPrompt("File could not be loaded.");
  }
  //#!endif

  // Load the data into our redux store
  if (isSample) {
    publishAction(rootOperations.loadSample(data));
  } else if (isUpdate) {
    publishAction(rootOperations.updateFile(data));
    publishAction(uiStateOperations.addNotification(notificationMessages.updatedFileHasBeenSyncedFromICloud));
  } else {
    publishAction(rootOperations.loadFile(data, convertFromV2));
  }

  if (convertFromStandard) {
    const convertAction = actionWithAppliedDate({
      type: rootActionTypes.CONVERT_STANDARD
    });
    publishAction(convertAction);

    //#!if harvard === true
    // Load preferences if Harvard to load the theme into the web presentation version
    if (data.preferences) {
      publishAction(preferencesOperations.loadPreferences(data.preferences));
    }
    //#!endif
  }

  //#!if readonly !== true
  // Needs to be dispatched separately because Undo/Redo occurs outside the batching system
// publishAction(undoRedoOperations.reset());
  //#!endif
};

export const showSpinnerAndLoadData = (inData, convertFromV2, convertFromStandard, isSample, isUpdate) => {
  // First, publish an action that will just show the spinner
  publishAction(welcomeOperations.showLoadingFile());
  // And on the next load cycle, tell it to go and load the actual data
  delay(loadData, 50, inData, convertFromV2, convertFromStandard, isSample, isUpdate);
};

const loadFile = (base64Str, convertFromV2, convertFromStandard, isUpdate) => {
  // Load the Data
  if (base64Str) {
    const jsonObj = decodeAndParseBase64Utf8(base64Str);
    showSpinnerAndLoadData(jsonObj, convertFromV2, convertFromStandard, false, isUpdate);
  }
};

const loadPreferenceData = base64Str => {
  const prefData = decodeAndParseBase64Utf8(base64Str);
  publishAction(preferencesOperations.loadPreferences(prefData));
};

const loadAutoUpdateData = base64Str => {
  const updateData = decodeAndParseBase64Utf8(base64Str);
  publishAction(autoUpdatesOperations.loadAutoUpdates(updateData));
};

export const exportTsfFile = (
  attachmentPath,
  attachmentIds,
  disableFiltering,
  disableFilteringMore,
  disableSplitView,
  disableTimelineZoom,
  skin
) => {
  publishAction(
    rootOperations.exportFileTsf(
      attachmentPath,
      attachmentIds,
      disableFiltering,
      disableFilteringMore,
      disableSplitView,
      disableTimelineZoom,
      skin
    )
  );
};

export const exportCsvFile = (delimiter, typeIds, isSelectedItemsOnly) => {
  publishAction(rootOperations.exportFileCsv(delimiter, typeIds, isSelectedItemsOnly));
};

/**
 * Show the exporting options modal
 */
export const exportCsv = () => {
  publishAction(uiStateOperations.showExportCsvModal());
};

const getToggleParentAction = (state, isExpanded) =>
  appStateOperations.toggleExpandSuperItem(
    appStateSelectors.getActiveViewId(state),
    appStateSelectors.getSelectedItems(state),
    isExpanded
  );

const sharedNativeCommsReceiver = (msg, data, store) => {
  // Get the current state out of the store
  const state = store.getState().present;

  switch (msg) {
    // Welcome Screen
    case nativeCommsMessageTypes.SHOW_WELCOME: {
      publishAction(welcomeOperations.showWelcome());
      return true;
    }
    case nativeCommsMessageTypes.WELCOME_UPDATE_ALL: {
      const parsedData = decodeAndParseBase64Utf8(data);
      publishAction(welcomeOperations.updateAll(parsedData));
      return true;
    }
    case nativeCommsMessageTypes.WELCOME_UPDATE_RECENT_FILES: {
      const parsedData = decodeAndParseBase64Utf8(data);
      publishAction(welcomeOperations.updateRecentFiles(parsedData));
      return true;
    }
    // Data Loading
    case nativeCommsMessageTypes.DATA_FROM_NATIVE:
    case nativeCommsMessageTypes.DATA_UPDATE_FROM_NATIVE:
    case nativeCommsMessageTypes.V2_DATA_FROM_NATIVE:
    case nativeCommsMessageTypes.STANDARD_DATA_FROM_NATIVE:
      loadFile(
        data,
        msg === nativeCommsMessageTypes.V2_DATA_FROM_NATIVE,
        msg === nativeCommsMessageTypes.STANDARD_DATA_FROM_NATIVE,
        msg === nativeCommsMessageTypes.DATA_UPDATE_FROM_NATIVE
      );
      return true;
    case nativeCommsMessageTypes.FILE_VERSION_TOO_LOW_FROM_NATIVE:
      publishAction(uiStateOperations.showFileVersionTooLowModal());
      break;

    // Set file name
    case nativeCommsMessageTypes.SET_FILE_NAME:
      publishAction(uiStateOperations.setFileName(data));
      return true;
    // Print Command
    case nativeCommsMessageTypes.FILE_PRINT:
      publishAction(uiStateOperations.showPrintPreviewModal());
      return true;

    // Options/Preferences Command
    case nativeCommsMessageTypes.SHOW_PREFERENCES:
      publishAction(uiStateOperations.showPreferencesModal());
      return true;

    // Show Backup Command
    case nativeCommsMessageTypes.SHOW_BACKUP:
      publishAction(uiStateOperations.showBackupModal());
      return true;

    // About Command
    case nativeCommsMessageTypes.SHOW_ABOUT:
      publishAction(uiStateOperations.showAboutModal());
      return true;

    // Export Command
    case nativeCommsMessageTypes.FILE_EXPORT_IMAGE:
      publishAction(uiStateOperations.showExportImagePreviewModal());
      return true;

    case nativeCommsMessageTypes.FILE_EXPORT_CSV: {
      exportCsv();
      return true;
    }
    case nativeCommsMessageTypes.FILE_EXPORT_TSF: {
      publishAction(uiStateOperations.showExportTsfModal());
      return true;
    }

    case nativeCommsMessageTypes.OPEN_SEARCH:
      publishAction(batchOperations.openSearch());
      return true;

    // View Commands
    case nativeCommsMessageTypes.SHOW_TIMELINE:
      publishAction(appStateOperations.switchViewType(TIMELINE_VIEW, null));
      return true;
    case nativeCommsMessageTypes.SHOW_NARRATIVE:
      publishAction(appStateOperations.switchViewType(NARRATIVE_VIEW, null));
      return true;
    case nativeCommsMessageTypes.SHOW_SPREADSHEET:
      publishAction(appStateOperations.switchViewType(SPREADSHEET_VIEW, null));
      return true;
    case nativeCommsMessageTypes.SHOW_OUTLINE:
      publishAction(appStateOperations.switchViewType(OUTLINE_VIEW, null));
      return true;
    case nativeCommsMessageTypes.SHOW_SUBWAY:
      publishAction(appStateOperations.switchViewType(SUBWAY_VIEW, null));
      return true;
    case nativeCommsMessageTypes.SHOW_RELATIONSHIPS:
      publishAction(appStateOperations.switchViewType(RELATIONSHIP_VIEW, null));
      return true;
    case nativeCommsMessageTypes.SHOW_MINDMAP:
      publishAction(appStateOperations.switchViewType(MINDMAP_VIEW, data));
      return true;
    case nativeCommsMessageTypes.SHOW_ITEM_LIST:
      publishAction(appStateOperations.toggleEntityList(data));
      return true;
    case nativeCommsMessageTypes.EXPAND_PARENT_ITEM: {
      publishAction(getToggleParentAction(state, true));
      return true;
    }
    case nativeCommsMessageTypes.COLLAPSE_PARENT_ITEM: {
      publishAction(getToggleParentAction(state, false));
      return true;
    }
    case nativeCommsMessageTypes.TOGGLE_GLOBAL_FILTER:
      publishAction(appStateOperations.toggleGlobalFilterPanel());
      return true;
    case nativeCommsMessageTypes.TOGGLE_LOCAL_FILTER:
      publishAction(appStateOperations.toggleLocalFilterPanel());
      return true;
    case nativeCommsMessageTypes.CLEAR_GLOBAL_FILTER:
      publishAction(filterOperations.clearFilter(GLOBAL_FILTER_ID));
      break;
    case nativeCommsMessageTypes.CLEAR_LOCAL_FILTER:
      publishAction(filterOperations.clearFilter(appStateSelectors.getActiveViewId(state)));
      break;
    case nativeCommsMessageTypes.SHOW_SETTINGS:
      publishAction(uiStateOperations.showSettingsModal());
      return true;

    // View -> Split Commands
    case nativeCommsMessageTypes.SPLIT_HORIZONTALLY: {
      publishAction(appStateOperations.splitViewHorizontal(appStateSelectors.getActiveViewId(state)));
      return true;
    }
    case nativeCommsMessageTypes.SPLIT_VERTICALLY: {
      publishAction(appStateOperations.splitViewVertical(appStateSelectors.getActiveViewId(state)));
      return true;
    }
    case nativeCommsMessageTypes.ADD_SPLIT_TOP:
      publishAction(appStateOperations.addView(DIRECTION_TOP));
      return true;
    case nativeCommsMessageTypes.ADD_SPLIT_BOTTOM:
      publishAction(appStateOperations.addView(DIRECTION_BOTTOM));
      return true;
    case nativeCommsMessageTypes.ADD_SPLIT_LEFT:
      publishAction(appStateOperations.addView(DIRECTION_LEFT));
      return true;
    case nativeCommsMessageTypes.ADD_SPLIT_RIGHT:
      publishAction(appStateOperations.addView(DIRECTION_RIGHT));
      return true;
    case nativeCommsMessageTypes.CLOSE_SPLIT: {
      publishAction(appStateOperations.removeView(appStateSelectors.getActiveViewId(state)));
      return true;
    }
    case nativeCommsMessageTypes.CLOSE_OTHER_SPLITS: {
      publishAction(appStateOperations.removeOtherViews());
      return true;
    }

    // Navigate Commands
    case nativeCommsMessageTypes.SWITCH_FOCUS:
      publishAction(appStateOperations.setNextActiveView(appStateSelectors.getActiveViewId(state)));
      return true;
    case nativeCommsMessageTypes.SCROLL_TO_DATE:
      //#!if iOS === true
// publishAction(appStateOperations.toggleScrollToDatePanel());
      //#!else
      publishAction(uiStateOperations.setMenuPopoverType(MENU_POPOVER_DATE));
      //#!endif
      return true;
    case nativeCommsMessageTypes.SCROLL_TO_SELECTION: {
      const selectedItems = uiStateSelectors.getSelectedItems(state);
      PubSub.publish(PubSubTopics.scrollToItem, { id: selectedItems.first() });
      return true;
    }

    // Navigate -> Selection Commands
    case nativeCommsMessageTypes.SELECT_ALL:
      if (isEditable(document.activeElement)) {
        document.activeElement.select();
      } else if (!uiStateSelectors.isModalOpen(state)) {
        PubSub.publish(PubSubTopics.selectItemByTopics, PubSubTopics.selectAllItems);
      }
      return true;
    case nativeCommsMessageTypes.SELECT_FIRST_ITEM:
      PubSub.publish(PubSubTopics.selectItemByTopics, PubSubTopics.selectFirstItem);
      return true;
    case nativeCommsMessageTypes.SELECT_PREVIOUS_ITEM:
      PubSub.publish(PubSubTopics.selectItemByTopics, PubSubTopics.selectPreviousItem);
      return true;
    case nativeCommsMessageTypes.SELECT_NEXT_ITEM:
      PubSub.publish(PubSubTopics.selectItemByTopics, PubSubTopics.selectNextItem);
      return true;
    case nativeCommsMessageTypes.SELECT_LAST_ITEM:
      PubSub.publish(PubSubTopics.selectItemByTopics, PubSubTopics.selectLastItem);
      return true;
    case nativeCommsMessageTypes.SELECT_ALL_BEFORE_CURRENT_SELECTION:
      PubSub.publish(PubSubTopics.selectItemByTopics, PubSubTopics.selectAllBeforeCurrentItem);
      return true;
    case nativeCommsMessageTypes.SELECT_ALL_AFTER_CURRENT_SELECTION:
      PubSub.publish(PubSubTopics.selectItemByTopics, PubSubTopics.selectAllAfterCurrentItem);
      return true;
    case nativeCommsMessageTypes.SELECT_NONE:
      publishAction(appStateOperations.clearSelection());
      return true;

    case nativeCommsMessageTypes.IS_FOCUS_ON_TEXT: {
      return isEditable(document.activeElement);
    }
    case nativeCommsMessageTypes.PRINT_DATA_FROM_NATIVE: {
      const inData = decodeAndParseBase64Utf8(data);
      // Incoming image data
      // TODO: If the width and height are not known, we need to calculate it here
      const imageData = Map(inData).map(d => {
        return immutableRecords.PrintImageRecord(d);
      });

      // Update any image dimensions that need updating
      // And pass a callback to publish the results
      updateImageDimensions(imageData, finalData => PubSub.publish(PubSubTopics.printWithLoadedImages, finalData));
      return true;
    }

    case nativeCommsMessageTypes.ALERT_PROMPT_RESPONSE:
      PubSub.publish(PubSubTopics.alertPromptResult, true);
      return true;
    case nativeCommsMessageTypes.OK_CANCEL_PROMPT_RESPONSE:
      PubSub.publish(PubSubTopics.okCancelPromptResult, JSON.parse(data));
      return true;
    case nativeCommsMessageTypes.YES_NO_PROMPT_RESPONSE:
      PubSub.publish(PubSubTopics.yesNoPromptResult, JSON.parse(data));
      return true;
    case nativeCommsMessageTypes.YES_NO_CANCEL_PROMPT_RESPONSE:
      PubSub.publish(PubSubTopics.yesNoCancelPromptResult, data);
      return true;

    case nativeCommsMessageTypes.BACKUP_DATA_RESULT:
      PubSub.publish(PubSubTopics.backupDataReceived, decodeAndParseBase64Utf8(data));
      return true;

    case nativeCommsMessageTypes.SET_FONT_PERCENTAGE:
      publishAction(appStateOperations.setFontPercentage(Number(data)));
      return true;
    case nativeCommsMessageTypes.SET_DEVICE_INSETS:
      publishAction(uiStateOperations.setDeviceInsets(decodeAndParseBase64(data)));
      return true;

    case nativeCommsMessageTypes.ENTER_EDIT_MODE: {
      publishAction(uiStateOperations.enterEditMode());
      let nativeMsg = {
        msg: nativeCommsBroadcastMessageTypes.EDIT_MODE_ENTERED
      };
      PubSub.publish(PubSubTopics.sendNativeMessage, nativeMsg);
      return true;
    }
    case nativeCommsMessageTypes.EXIT_EDIT_MODE: {
      publishAction(batchOperations.exitEditModeAndClearSelection());
      let nativeMsg = {
        msg: nativeCommsBroadcastMessageTypes.EDIT_MODE_EXITED
      };
      PubSub.publish(PubSubTopics.sendNativeMessage, nativeMsg);
      return true;
    }

    case nativeCommsMessageTypes.UPDATE_LICENSE_STATUS: {
      const parsedData = decodeAndParseBase64Utf8(data);
      publishAction(registrationOperations.updateRegistrationStatus(parsedData));
      if (process.env.NODE_ENV === "production" && window.sendToSentry) {
        Sentry.setUser({ id: parsedData.licenseCode, email: parsedData.emailAddress });
      }
      return true;
    }
    case nativeCommsMessageTypes.CHECK_FOR_LICENSED_UPDATE_RESPONSE: {
      const parsedData = decodeAndParseBase64Utf8(data);
      const updateData = {
        isChecking: false,
        ...parsedData
      };
      publishAction(autoUpdatesOperations.loadAutoUpdates(updateData));
      return true;
    }
    case nativeCommsMessageTypes.SHOW_RELEASE_NOTES: {
      const parsedData = decodeAndParseBase64(data);
      const updateData = {
        showUpdateModal: false,
        isChecking: false,
        isEditingUpdateAvailable: false,
        isReadonlyUpdateAvailable: false,
        updateRequiresShellUpdate: false,
        availableReleaseNotes: parsedData.html,
        isReleaseNotesNew: parsedData.isNew
      };
      publishAction(autoUpdatesOperations.loadAutoUpdates(updateData));
      return true;
    }
    case nativeCommsMessageTypes.WEBSITE_UPDATES_RESPONSE: {
      const parsedData = decodeAndParseBase64Utf8(data);
      PubSub.publish(PubSubTopics.websiteUpdatesResponse, parsedData);
      return true;
    }
    case nativeCommsMessageTypes.USE_TEXT_SIZE_SMALL:
      publishAction(preferencesOperations.setGlobalFontSize(GlobalFontSizes.Small));
      return true;

    case nativeCommsMessageTypes.USE_TEXT_SIZE_MEDIUM:
      publishAction(preferencesOperations.setGlobalFontSize(GlobalFontSizes.Medium));
      return true;

    case nativeCommsMessageTypes.USE_TEXT_SIZE_LARGE:
      publishAction(preferencesOperations.setGlobalFontSize(GlobalFontSizes.Large));
      return true;

    case nativeCommsMessageTypes.USE_TEXT_SIZE_XLARGE:
      publishAction(preferencesOperations.setGlobalFontSize(GlobalFontSizes.XLarge));
      return true;

    case nativeCommsMessageTypes.USE_TEXT_SIZE_XXLARGE:
      publishAction(preferencesOperations.setGlobalFontSize(GlobalFontSizes.XXLarge));
      return true;

    case nativeCommsMessageTypes.PREFERENCE_DATA_FROM_NATIVE:
      loadPreferenceData(data);
      return true;
    case nativeCommsMessageTypes.AUTO_UPDATE_DATA_FROM_NATIVE:
      loadAutoUpdateData(data);
      return true;
    case nativeCommsMessageTypes.SET_FONT_SERVER_URL:
      window.fontServerUrl = decodeBase64Utf8(data);
      return true;

    case nativeCommsMessageTypes.ENABLE_SENTRY_LOG: {
      window.sendToSentry = true;
      return true;
    }

    default:
      return false;
  }
};

export default sharedNativeCommsReceiver;
